import * as React from 'react';
import styled from 'styled-components';
import { ListGroupItem } from 'reactstrap';

export const SurveyCard = styled(ListGroupItem)`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--spacer-S);
    padding: var(--spacer-S) var(--spacer-M);
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: var(--bg-light);
    }
`;

export const SurveyMeta = styled.div`
    flex: 1;
    min-width: 0;
`;

export const SurveyName = styled.div`
    font-weight: 600;
    color: var(--text-body);
    font-size: 0.95rem;
`;

export const SurveyDate = styled.div`
    font-size: 0.78rem;
    color: var(--text-muted);
    margin-top: var(--spacer-XXS);
`;

export const SurveyFieldBadges = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacer-XS);
    margin-top: var(--spacer-XS);
`;

export const SurveyActions = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XXS);
    flex-shrink: 0;
`;


export const FieldRow = styled.div`
    border: var(--border-thin) solid var(--appLigthGrey);
    border-left: var(--border-thick) solid var(--brand-secondary);
    border-radius: 8px;
    padding: var(--spacer-S);
    background: var(--bg-light);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

export const FieldRowHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

export const FieldRowTitle = styled.span`
    font-weight: 600;
    color: var(--text-body);
    font-size: 0.875rem;
`;

export const ScaleRangeRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
`;

export const EmptyState = styled.div`
    padding: var(--spacer-S);
    color: var(--text-muted);
    font-style: italic;
    font-size: 0.9rem;
`;

export const AddSurveyRow = styled(ListGroupItem)`
    background: transparent;
`;

export const SurveyStats = styled.div`
    display: flex;
    gap: var(--spacer-S);
    margin-top: var(--spacer-XS);
`;

export const SurveyStatChip = styled.span`
    font-size: 0.75rem;
    color: var(--text-muted);
    background: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 4px;
    padding: 1px var(--spacer-XS);
`;

// ─── Channel Card Styled Components ──────────────────────────────────────────

export interface ChannelCardProps extends React.HTMLAttributes<HTMLDivElement> {
    $isActive: boolean;
    $isSelected?: boolean;
}

export const ChannelCard = styled.div.attrs<ChannelCardProps>(props => ({
    ...props,
}))<ChannelCardProps>`
    border: var(--border-thin) solid ${props => props.$isSelected ? 'var(--appBlue)' : 'var(--appLigthGrey)'};
    border-radius: 8px;
    margin-bottom: var(--spacer-S);
    overflow: hidden;
    transition: border-color 0.2s ease;
`;

export interface ChannelHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    $isActive: boolean;
    $isSelected: boolean;
}

export const ChannelHeader = styled.div.attrs<ChannelHeaderProps>(props => ({
    ...props,
}))<ChannelHeaderProps>`
    display: flex;
    align-items: center;
    padding: var(--spacer-S);
    background-color: ${props => props.$isSelected ? 'var(--bg-hover-light)' : 'var(--bg-light)'};
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: var(--bg-hover-light);
    }
`;

export const ChannelInfo = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    flex-wrap: wrap;
    flex: 1;
    margin-left: var(--spacer-XS);
`;

export const ChannelContent = styled.div`
    padding: var(--spacer-S);
    border-top: var(--border-thin) solid var(--appLigthGrey);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

export interface ChannelStatusProps extends React.HTMLAttributes<HTMLSpanElement> {
    $isActive: boolean;
}

export const ChannelStatus = styled.span.attrs<ChannelStatusProps>(props => ({
    ...props,
}))<ChannelStatusProps>`
    font-size: 0.75rem;
    padding: 2px 8px;
    border-radius: 12px;
    background-color: ${props => props.$isActive ? 'var(--success-bg)' : 'transparent'};
    color: ${props => props.$isActive ? 'var(--success-color)' : 'var(--text-muted)'};
    border: var(--border-thin) solid ${props => props.$isActive ? 'var(--success-border)' : 'var(--appLigthGrey)'};
`;

// ─── AI Generation Styled Components ─────────────────────────────────────────

export const ModeSelectorContainer = styled.div`
    display: flex;
    gap: var(--spacer-M);
    justify-content: center;
    padding: var(--spacer-M) var(--spacer-S);
`;

export interface ModeButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    $ai?: boolean;
}

export const ModeButton = styled.button.attrs<ModeButtonProps>(props => ({
    ...props,
}))<ModeButtonProps>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacer-XS);
    padding: var(--spacer-M) var(--spacer-L);
    border: var(--border-medium) solid ${p => p.$ai ? 'var(--info-color)' : 'var(--appLigthGrey)'};
    border-radius: 8px;
    background: ${p => p.$ai ? 'rgba(102, 126, 234, 0.06)' : 'white'};
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;
    color: ${p => p.$ai ? 'var(--info-color)' : 'var(--text-body)'};
    min-width: 160px;
    transition: border-color 0.15s, background 0.15s, color 0.15s;

    &:hover {
        border-color: ${p => p.$ai ? 'var(--info-color)' : 'var(--appBlue)'};
        background: ${p => p.$ai ? 'rgba(102, 126, 234, 0.12)' : 'var(--bg-hover-light)'};
        color: ${p => p.$ai ? 'var(--info-color)' : 'var(--appBlue)'};
    }

    svg {
        font-size: 2rem;
    }
`;

export const AISection = styled.div`
    display: flex;
    flex-direction: column;
    gap: var(--spacer-S);
`;

export const GeneratingOverlay = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacer-S);
    padding: var(--spacer-L);
    color: var(--info-color);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 4px;
    background: var(--bg-light);
`;

export const GeneratingLabel = styled.div`
    font-size: 0.95rem;
    color: var(--text-muted);
`;

// ─── Share Modal Styled Components ───────────────────────────────────────────

export const ShareIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: var(--bg-hover-light);
    color: var(--appBlue);
    flex-shrink: 0;

    svg {
        font-size: 1.5rem;
    }
`;

export const ShareLinkRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
    background: var(--bg-light);
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 8px;
    padding: var(--spacer-XS) var(--spacer-S);
`;

export const ShareLinkText = styled.span`
    flex: 1;
    font-size: 0.85rem;
    color: var(--text-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: monospace;
`;

export const ShareSectionDivider = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    color: var(--text-muted);
    font-size: 0.8rem;

    &::before, &::after {
        content: '';
        flex: 1;
        height: var(--border-thin);
        background: var(--appLigthGrey);
    }
`;

export const ShareSendHeader = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-S);
    margin-bottom: var(--spacer-XS);
`;

export const ShareSendTitle = styled.div`
    font-weight: 600;
    font-size: 0.95rem;
    color: var(--text-body);
`;
